import { useState } from 'react';

import { IconSetting } from '@iwoca/icons/brand/mono';
import { Button, Drawer, Switch } from '@iwoca/orion';

import { TabConfig, TableSettingKey } from '../../CustomersV2.types';
import {
  defaultTableSettings,
  TableSettings,
} from '../../hooks/useTableSettings';
import { trackTableSettingToggled } from '../../tracking';

export const TableSettingsControl = ({
  config,
  settings,
  onApply,
}: {
  config: TabConfig;
  settings: TableSettings;
  onApply: (next: TableSettings) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [staged, setStaged] = useState<TableSettings>(settings);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setStaged(settings);
  }

  if (config.settings.length === 0) return null;

  const toggle = (key: TableSettingKey) =>
    setStaged((prev) => ({ ...prev, [key]: !prev[key] }));

  const apply = () => {
    // One event per setting the seller actually changed, so an Apply that
    // altered nothing is silent and flipping both toggles counts as two.
    // Compared against `settings` rather than the staged snapshot taken on open,
    // so the events describe the change being committed now.
    for (const { key } of config.settings) {
      if (staged[key] !== settings[key]) {
        trackTableSettingToggled({
          tab: config.path,
          setting: key,
          enabled: staged[key],
        });
      }
    }
    onApply(staged);
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        aria-label="Table settings"
        onClick={() => setIsOpen(true)}
        className="flex shrink-0 cursor-pointer items-center gap-s rounded-2xl border-none bg-secondary-95 px-m py-s text-secondary-40"
      >
        <IconSetting height={20} width={20} className="text-secondary-40" />
      </button>

      <Drawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Table settings"
      >
        <div className="flex flex-col gap-l pb-[112px]">
          {config.settings.map((setting) => (
            <div
              key={setting.key}
              className="flex items-center justify-between gap-m rounded-s border border-structure-80 bg-structure-100 p-m"
            >
              <span className="text-s font-med text-primary-10">
                {setting.label}
              </span>
              <Switch
                aria-label={setting.label}
                checked={staged[setting.key]}
                onClick={() => toggle(setting.key)}
              />
            </div>
          ))}
          <Button
            variant="secondary-alt"
            className="w-full"
            onClick={() => setStaged(defaultTableSettings(config))}
          >
            Reset settings to default
          </Button>
        </div>

        <div className="absolute inset-x-0 bottom-0 bg-structure-100 p-xl shadow-black-l">
          <Button className="w-full" onClick={apply}>
            Apply
          </Button>
        </div>
      </Drawer>
    </>
  );
};
